import { useState, useEffect } from "react";
import {
  getDesignRequest,
  getPrintFilesByDesignRequestId,
  getGalleryImagesByDesignRequestId,
  getProofImagesByDesignRequestId,
  restartDesignRequest,
  getOrganizationBySalesForceId,
  getRequestLogByRequestId,
  getMember,
} from "../../services/axios";
import { useHistory } from "react-router-dom";
import { ImageCard } from "../../components/image-card";
import { toast } from "sonner";
import { designAreaLabel } from "../../utils/design-areas";
import { CSVLink } from "react-csv";
import { useAlert } from "../../hooks/useAlert";
import { Member } from "../../utils/types";

/*
status_codes
0 - neutral
1 - good
2 - warn
3 - error
*/

type LOGS = {
  id: string;
  request_id: string;
  status: string;
  status_code: number;
  created_at: string;
};

type IMAGE_TYPE = {
  id: string;
  pk: string;
  file: string;
  file_type: string;
  campaign: string;
  collection: string;
  request_id: string;
  job: string;
  created_at: string;
  updated_at: string;
};

type PRODUCT_VARIANTS = {
  color_name: string;
  color_hex: string;
};

type DESIGN_REQUEST = {
  id: string;
  pk: string;
  status: string;
  campaign: string;
  design_file_url: string;
  design_file_name: string;
  design_file_id: string;
  product_template_name: string;
  product_template_url: string;
  product_template_brand: string;
  product_template_model: string;
  product_template_sku: string;
  configured: false;
  configured_variants: PRODUCT_VARIANTS[];
  configured_layers: string[];
  configured_design_area: string;
  configured_athletes: string[];
  configured_organizations: string[];
  configured_data_url: string;
  request_priority: 0;
  request_job_url: string;
  request_id: string;
  created_at: string;
  updated_at: string;
};

type ORGANIZATION_TYPE = {
  website: string;
  affinity_organization_id: string;
  motto_short: string;
  national: string;
  motto: string;
  motto_long: string;
  colloquial_phrase: string;
  colloquial_name: string;
  salesforce_id: string;
  full_name: string;
  menu_name: string;
  acronym_name: string;
  state: string;
  city: string;
  account_name: string;
  founding_year: string;
  default_name: string;
  colloquial_letters: string;
  licensing: string[];
  category: string;
  id: string;
  tags: string[];
  parents: string[];
};

export const Design = () => {
  // const { products } = useSelector((state: RootState) => state.vendorProducts)

  const alert = useAlert();

  let [state, setState] = useState<any>({
    visible: "print",
  });

  let [proofData, setProofData] = useState<any>([]);
  let [galleryData, setGalleryData] = useState<any>([]);
  let [printData, setPrintData] = useState<any>([]);

  let [printFiles, setPrintFiles] = useState<IMAGE_TYPE[]>([]);
  let [galleryImages, setGalleryImages] = useState<IMAGE_TYPE[]>([]);
  let [proofGroupImages, setproofGroupImages] = useState<[]>([]);

  let [logs, setLogs] = useState<LOGS[]>([]);

  /*
    status: "start",
    name: state.name,
    template_url: state.template_url,
    template_name: state.template_name,
    configurable: false,
    original_file_name: state.original_file_name,
    proof_template: selectedProofTemplate.value.template_name,
    color_csv_name: selectedProofTemplate.value.color_csv_name,
    design_area: selectedDesignArea.value,
    campaign: typeCampaignId? state.template_name : state.campaign,
    stores: ['greekhouse'],
    timestamp: new Date()
    */

  let [design, setDesign] = useState<DESIGN_REQUEST>({
    id: "",
    pk: "",
    status: "",
    campaign: "",
    design_file_url: "",
    design_file_name: "",
    design_file_id: "",
    product_template_name: "",
    product_template_url: "",
    product_template_brand: "",
    product_template_model: "",
    product_template_sku: "",
    configured: false,
    configured_variants: [],
    configured_layers: [],
    configured_design_area: "",
    configured_athletes: [],
    configured_organizations: [],
    configured_data_url: "",
    request_priority: 0,
    request_job_url: "",
    request_id: "",
    created_at: "",
    updated_at: "",
  });

  let history = useHistory();
  let ID = history.location.search.split("=")[1];

  useEffect(() => {
    const fetchProduct = async (ID: string) => {
      const result = await getDesignRequest(ID);
      const typedResult = result as DESIGN_REQUEST;

      setDesign({
        id: ID,
        pk: typedResult.pk,
        status: typedResult.status,
        campaign: typedResult.campaign,
        design_file_url: typedResult.design_file_url,
        design_file_name: typedResult.design_file_name,
        design_file_id: typedResult.design_file_id,
        product_template_name: typedResult.product_template_name,
        product_template_url: typedResult.product_template_url,
        product_template_brand: typedResult.product_template_brand,
        product_template_model: typedResult.product_template_model,
        product_template_sku: typedResult.product_template_sku,
        configured: typedResult.configured,
        configured_variants: typedResult.configured_variants,
        configured_layers: typedResult.configured_layers,
        configured_design_area: typedResult.configured_design_area,
        configured_athletes: typedResult.configured_athletes,
        configured_organizations: typedResult.configured_organizations,
        configured_data_url: typedResult.configured_data_url,
        request_priority: typedResult.request_priority,
        request_job_url: typedResult.request_job_url,
        request_id: typedResult.request_id,
        created_at: typedResult.created_at,
        updated_at: typedResult.updated_at,
      });

      const fetchPrintFiles = async (template_name: string) => {
        const result = await getPrintFilesByDesignRequestId(template_name);
        const typedResult = result as IMAGE_TYPE[];
        setPrintFiles(typedResult);
      };

      const fetchGalleryImages = async (template_name: string) => {
        const result = await getGalleryImagesByDesignRequestId(template_name);
        const typedResult = result as IMAGE_TYPE[];
        setGalleryImages(typedResult);
      };

      const fetchProofImages = async (template_name: string) => {
        const result = await getProofImagesByDesignRequestId(template_name);
        const typedResult = result as IMAGE_TYPE[];
        groupByColor(typedResult);
      };

      const fetchLogs = async (template_name: string) => {
        const result = await getRequestLogByRequestId(template_name);
        const typedResult = result as LOGS[];
        setLogs(
          typedResult
            .sort((a, b) =>
              a.created_at
                .split("/")
                .reverse()
                .join()
                .localeCompare(b.created_at.split("/").reverse().join())
            )
            .reverse()
            .slice(0, 5)
        );
      };

      fetchPrintFiles(ID);
      fetchGalleryImages(ID);
      fetchProofImages(ID);
      fetchLogs(ID);
    };

    fetchProduct(ID);
  }, [proofGroupImages.length]);

  function goBack() {
    history.push({
      pathname: `/campaign/${design.campaign}`,
    });
  }

  function showTab(tab: any) {
    setState({
      ...state,
      visible: tab,
    });
  }

  function groupByColor(images: any) {
    const proofGroupImagesAvailability: any = {};
    if (images && images.length > 0) {
      const grpImages: any = images.reduce(function (r: any, a: any) {
        let file: any = a.file.split("-");
        let color: any = file[file.length - 1].split(".")[0];
        r[color] = r[color] || [];
        r[color].push(a);
        proofGroupImagesAvailability[color] = a.isAvilable;
        return r;
      }, Object.create([]));
      setproofGroupImages(grpImages);
    }
  }

  useEffect(() => {
    fetchProofData();
    fetchGalleryData();
    fetchPrintData();
  }, [proofGroupImages, galleryImages, design]);

  const fetchPrintData = async () => {
    const res = await Promise.all(
      await printFiles?.map(async (proof: any) => {
        const index = Number(proof.job.split("_")[1]);

        let organizationId = "";
        if (
          design.configured_organizations &&
          design.configured_organizations.length <= 1
        ) {
          organizationId = design
            .configured_organizations[0] as unknown as string;
        } else {
          organizationId = design.configured_organizations[
            index
          ] as unknown as string;
        }
        const organization = (await getOrganizationBySalesForceId(
          organizationId
        )) as unknown as ORGANIZATION_TYPE;

        let athleteId = "";
        if (
          design.configured_athletes &&
          design.configured_athletes.length <= 1
        ) {
          athleteId = design.configured_athletes[0] as unknown as string;
        } else {
          athleteId = design.configured_athletes[index] as unknown as string;
        }
        const athlete = await getMember(athleteId);

        return {
          ...proof,
          location: design?.configured_design_area
            ? design?.configured_design_area
            : "",
          company: {
            ...organization,
          },
          athlete: {
            ...athlete,
          },
        };
      })
    );
    const nextPrintData = res.map((result: any) => {
      return {
        "Campaign ID": design.campaign,
        "Organization Name": result?.company?.default_name,
        "Athlete Name": result?.athlete?.full_name,
        "Account Name": result?.company?.account_name,
        Category: result?.company?.category,
        "File Link": result.file,
        Location: result.location,
      };
    });
    setPrintData(nextPrintData.flatMap((item) => item));
  };

  const fetchProofData = async () => {
    const nextProofData = await Promise.all(
      Object.entries(proofGroupImages).flatMap(async (innerAttr: any) => {
        const [color, proofs] = innerAttr;

        const res = await Promise.all(
          proofs?.map(async (proof: any) => {
            const index = Number(proof.job.split("_")[1]);

            let organizationId = "";
            if (
              design.configured_organizations &&
              design.configured_organizations.length <= 1
            ) {
              organizationId = design
                .configured_organizations[0] as unknown as string;
            } else {
              organizationId = design.configured_organizations[
                index
              ] as unknown as string;
            }
            const organization = (await getOrganizationBySalesForceId(
              organizationId
            )) as unknown as ORGANIZATION_TYPE;

            let athleteId = "";
            if (
              design.configured_athletes &&
              design.configured_athletes.length <= 1
            ) {
              athleteId = design.configured_athletes[0] as unknown as string;
            } else {
              athleteId = design.configured_athletes[
                index
              ] as unknown as string;
            }
            const athlete = await getMember(athleteId);

            return {
              ...proof,
              location: design?.configured_design_area
                ? design?.configured_design_area
                : "",
              company: {
                ...organization,
              },
              athlete: {
                ...athlete,
              },
            };
          })
        );
        const next = res.map((result: any) => {
          return {
            "Campaign ID": design.campaign,
            "Organization Name": result?.company?.default_name,
            "Athlete Name": result?.athlete?.full_name,
            "Account Name": result?.company?.account_name,
            Category: result?.company?.category,
            "File Link": result.file,
            Location: result.location,
          };
        });
        return next;
      })
    );
    setProofData(nextProofData.flatMap((item) => item));
  };

  const fetchGalleryData = async () => {
    const res = await Promise.all(
      await galleryImages?.map(async (proof: any) => {
        const index = Number(proof.job.split("_")[1]);

        let organizationId = "";
        if (
          design.configured_organizations &&
          design.configured_organizations.length <= 1
        ) {
          organizationId = design
            .configured_organizations[0] as unknown as string;
        } else {
          organizationId = design.configured_organizations[
            index
          ] as unknown as string;
        }
        const organization = (await getOrganizationBySalesForceId(
          organizationId
        )) as unknown as ORGANIZATION_TYPE;

        let athleteId = "";
        if (
          design.configured_athletes &&
          design.configured_athletes.length <= 1
        ) {
          athleteId = design.configured_athletes[0] as unknown as string;
        } else {
          athleteId = design.configured_athletes[index] as unknown as string;
        }
        const athlete = await getMember(athleteId);

        return {
          ...proof,
          location: design?.configured_design_area
            ? design?.configured_design_area
            : "",
          company: {
            ...organization,
          },
          athlete: {
            ...athlete,
          },
        };
      })
    );
    const nextGalleryData = res.map((result: any) => {
      return {
        "Campaign ID": design.campaign,
        "Organization Name": result?.company?.default_name,
        "Athlete Name": result?.athlete?.full_name,
        "Account Name": result?.company?.account_name,
        Category: result?.company?.category,
        "File Link": result.file,
        Location: result.location,
      };
    });
    setGalleryData(nextGalleryData.flatMap((item) => item));
  };

  function RenderImagePanel() {
    switch (state.visible) {
      case "print":
        return (
          <>
            <div className="flex flex-wrap">
              {printFiles ? (
                printFiles.map(function (image, iOne) {
                  return (
                    <div key={iOne} className="w-52 m-2">
                      <ImageCard {...image} />
                    </div>
                  );
                })
              ) : (
                <div>None Found</div>
              )}
            </div>
            <CSVLink
              data={printData}
              asyncOnClick={true}
              onClick={(event, done) => {
                if (
                  design?.configured_organizations?.length === 0 ||
                  printFiles.length === 0
                ) {
                  done(false); // Don't Proceed
                  alert.showAlert(
                    "Error downloading!",
                    "There are no organizations related to any of these images",
                    "Error"
                  );
                  return;
                }

                done();
              }}
              className="mt-8 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Export Print Data
            </CSVLink>
          </>
        );
      case "gallery":
        return (
          <>
            <div className="flex flex-wrap">
              {galleryImages ? (
                galleryImages.map(function (image, iTwo) {
                  return (
                    <div key={iTwo} className="flex flex-col m-2 space-x-2">
                      <div className="w-52">
                        <ImageCard {...image} />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div>None Found</div>
              )}
            </div>
            <CSVLink
              data={galleryData}
              asyncOnClick={true}
              onClick={(event, done) => {
                if (
                  design?.configured_organizations?.length === 0 ||
                  galleryData.length === 0
                ) {
                  done(false); // Don't Proceed
                  alert.showAlert(
                    "Error downloading!",
                    "There are no organizations related to any of these images",
                    "Error"
                  );
                  return;
                }

                done();
              }}
              className="mt-8 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Export Gallery Data
            </CSVLink>
          </>
        );
      case "proof":
        return (
          <div>
            <div>
              {proofGroupImages &&
                Object.entries(proofGroupImages).map((innerAttr: any) => {
                  return (
                    <div
                      className="flex"
                      style={{ maxHeight: "350px" }}
                      key={innerAttr[0]}
                    >
                      <div className="flex">
                        {innerAttr[1].map((image: any, i: number) => {
                          return (
                            <div
                              key={innerAttr[0] + "-" + i}
                              className="flex w-52 m-2"
                            >
                              <ImageCard {...image} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>

            <CSVLink
              data={proofData}
              asyncOnClick={true}
              onClick={(event, done) => {
                if (
                  design?.configured_organizations?.length === 0 ||
                  proofData.length === 0
                ) {
                  done(false); // Don't Proceed
                  alert.showAlert(
                    "Error downloading!",
                    "There are no organizations related to any of these images",
                    "Error"
                  );
                  return;
                }

                done();
              }}
              className="mt-8 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Export Proof Data
            </CSVLink>
            <div className="flex flex-wrap"></div>
          </div>
        );
    }
  }

  function restartTasks(design: string) {
    return restartDesignRequest(design)
      .then(() => {
        toast.success(`started!`, { duration: 5000 });
      })
      .catch((e) => toast.error(e.message, { duration: 5000 }));
  }

  return (
    <div className="m-8">
      <div
        className="underline cursor-pointer text-emerald-600 mb-10"
        onClick={() => goBack()}
      >
        Back to Campaign
      </div>
      <div className="font-bold pb-4">{ID}</div>
      <div className="flex flex-row w-1/2 pb-4 space-x-2">
        <button
          className="px-4 py-2 rounded border"
          onClick={() => restartTasks(design.id)}
        >
          Restart
        </button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <table className="table-auto w-full border-collapse border border-emerald-800">
          <tbody>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Template Name
              </td>
              <td className="px-4 border border-emerald-600">
                {design.request_id}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Status
              </td>
              <td className="px-4 border border-emerald-600">
                {design.status}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Configurable
              </td>
              <td className="px-4 border border-emerald-600">
                {design.configured_layers.join(",")}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Campaign
              </td>
              <td className="px-4 border border-emerald-600">
                {design.campaign}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Original File Name
              </td>
              <td className="px-4 border border-emerald-600">
                {design.design_file_name}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Design Area
              </td>
              <td className="px-4 border border-emerald-600">
                {design.configured_design_area
                  ? designAreaLabel(design.configured_design_area)
                  : "no design area"}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Number of Configurable Organizations
              </td>
              <td className="px-4 border border-emerald-600">
                {design.configured_organizations
                  ? design.configured_organizations.length
                  : "0"}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Number of Configurable Athletes
              </td>
              <td className="px-4 border border-emerald-600">
                {design.configured_athletes
                  ? design.configured_athletes.length
                  : "0"}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                brand
              </td>
              <td className="px-4 border border-emerald-600">
                {design.product_template_brand
                  ? design.product_template_brand
                  : "no brand selected"}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                model
              </td>
              <td className="px-4 border border-emerald-600">
                {design.product_template_model
                  ? design.product_template_model
                  : "no model selected"}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">sku</td>
              <td className="px-4 border border-emerald-600">
                {design.product_template_sku
                  ? design.product_template_sku
                  : "no product selected"}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Priority
              </td>
              <td className="px-4 border border-emerald-600">
                {design.request_priority}
              </td>
            </tr>
            <tr className="p-2 m-2">
              <td className="px-4 border border-emerald-600 font-bold">
                Job Data URL
              </td>
              <td className="px-4 border border-emerald-600">
                {design.request_job_url
                  ? design.request_job_url
                  : "no job url available"}
              </td>
            </tr>
          </tbody>
        </table>
        <table className="table-auto w-full border-collapse border border-emerald-800">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                <div className="group inline-flex">Status</div>
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                <div className="group inline-flex">Time</div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {logs.map(function (log, index) {
              return (
                <tr key={index} className="p-2 m-2 group">
                  <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900 flex flex-row items-center">
                    {log.status_code === 0 ? (
                      <div className="bg-gray-100 h-2 w-2 rounded-full mr-4"></div>
                    ) : (
                      <></>
                    )}
                    {log.status_code === 1 ? (
                      <div className="bg-emerald-600 h-2 w-2 rounded-full mr-4"></div>
                    ) : (
                      <></>
                    )}
                    {log.status_code === 2 ? (
                      <div className="bg-orange-600 h-2 w-2 rounded-full mr-4"></div>
                    ) : (
                      <></>
                    )}
                    {log.status_code === 3 ? (
                      <div className="bg-red-600 h-2 w-2 rounded-full mr-4"></div>
                    ) : (
                      <></>
                    )}
                    {log.status}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">
                    {log.created_at}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Download All Section*/}
      {/* Display All Slider*/}
      <div className="flex flex-wrap border-b mt-2 pt-2">
        <div
          className="cursor-pointer border-l border-t border-r rounded mr-2 ml-2 p-2 bg-gray-50"
          onClick={() => showTab("print")}
        >
          Print Files
        </div>
        <div
          className="cursor-pointer border-l border-t border-r rounded mr-2 ml-2 p-2 bg-gray-50"
          onClick={() => showTab("gallery")}
        >
          Gallery Images
        </div>
        <div
          className="cursor-pointer border-l border-t border-r rounded mr-2 ml-2 p-2 bg-gray-50"
          onClick={() => showTab("proof")}
        >
          Proof Images
        </div>
      </div>
      <div>
        <div className="pt-2 mt-2">{RenderImagePanel()}</div>
      </div>
    </div>
  );
};
